import { createContext, useContext, useEffect, useState } from 'react';
import { tenantsApi } from '../api/tenants.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { auth } from '../auth/firebase.js';

const Ctx = createContext(null);
const KEY_PREFIX = 'qubby-admin:hostId:';

function loadStored(uid) {
    if (!uid) return '';
    return localStorage.getItem(KEY_PREFIX + uid) || '';
}
function saveStored(uid, hostId) {
    if (!uid) return;
    if (hostId) localStorage.setItem(KEY_PREFIX + uid, hostId);
    else localStorage.removeItem(KEY_PREFIX + uid);
}

/** 給 TenantProvider 外層的元件用（BuildJobsProvider 掛在 TenantProvider 之上，
 *  拿不到 context 但仍需知道「目前在哪個 tenant」才能把建構卡限縮在本租戶）。 */
export function storedHostId(uid) {
    return loadStored(uid);
}

export function TenantProvider({ children }) {
    const { user } = useAuth();
    const uid = user?.uid;

    const [tenants, setTenants] = useState([]);
    const [hostId, setHostId] = useState(() => loadStored(uid));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accountRole, setAccountRole] = useState(null);   // super_admin | dealer | business | null
    const [partnerSource, setPartnerSource] = useState(null);   // 'sdx500' → 顯示續約/加購頁
    const [sharedHostId, setSharedHostId] = useState(null);     // 共用租戶的商用子帳號（users.sharedHostId）
    const [entitlement, setEntitlement] = useState(null);   // { seats, roleQuota, capabilities, plan } 來自 quota endpoint

    // opts.silent=true：背景重抓（不閃 loading、保留畫面上的舊資料直到新資料回來）——
    // 給「進入租戶設定頁自動刷新」用；SDX500 東訊後台改分機後，切回本頁就能看到最新分機
    // （不用整頁重整）。onClick={reload} 傳進來的是 click event，event.silent=undefined → 照舊全量。
    async function reload(opts = {}) {
        if (opts.silent !== true) setLoading(true);
        try {
            // /me：拿 accountRole（super_admin 顯示 /admin 入口）+ partnerSource（東訊帳號顯示續約/加購頁）
            try {
                const idToken = await auth.currentUser?.getIdToken();
                const me = await fetch('/me', { headers: { Authorization: `Bearer ${idToken}` } });
                if (me.ok) { const j = await me.json(); setAccountRole(j.accountRole || null); setPartnerSource(j.partnerSource || null); setSharedHostId(j.sharedHostId || null); }
            } catch { /* accountRole 拿不到不影響一般功能 */ }
            const r = await tenantsApi.list();
            const list = r?.tenants || [];
            setTenants(list);
            // 若目前 hostId 不在 user 的 tenant 列表 → 自動切到第一個（並 clear stale store）
            if (list.length === 0) {
                setHostIdAndPersist('');
            } else if (!list.find((t) => t.hostId === hostId)) {
                setHostIdAndPersist(list[0].hostId);
            }
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }

    function setHostIdAndPersist(id) {
        setHostId(id);
        saveStored(uid, id);
    }

    // user 變了（不同帳號登入）→ 重置 hostId 從該 user 的 storage 取
    useEffect(() => {
        setHostId(loadStored(uid));
        if (uid) reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    // 目前 tenant 的方案/席次（判斷有沒有買 CS 席次 → 灰掉 CS UI）
    useEffect(() => {
        if (!hostId) { setEntitlement(null); return; }
        let cancelled = false;
        tenantsApi.quota(hostId)
            .then((q) => { if (!cancelled) setEntitlement(q || null); })
            .catch(() => { if (!cancelled) setEntitlement(null); });
        return () => { cancelled = true; };
    }, [hostId]);

    const currentTenant = tenants.find((t) => t.hostId === hostId) || null;
    const role = currentTenant?._role || null;
    const isSuperAdmin = accountRole === 'super_admin';
    const isAdmin = role === 'admin' || isSuperAdmin;
    // 對應後端 tenantGuard 'operate'：admin / agent 可操作（外撥、貼標籤…），viewer 唯讀；刪除類看 isAdmin
    const canOperate = isAdmin || role === 'agent';
    // 共用租戶的商用子帳號：在「共用的那個租戶」裡是 admin 但資料分區、租戶設定唯讀
    const isSharedBusiness = currentTenant?._sharedBusiness === true;

    // hasCS：只有「明確拿到 roleQuota/seats 且 CS(plan_02)=0」才視為無 CS（灰掉 CS 功能）；
    // 拿不到資料（舊帳號 / 超管 / 經銷商 / quota 讀失敗）一律預設有，避免誤傷。
    const csRole = entitlement?.roleQuota?.plan_02;
    const csSeat = entitlement?.seats?.plan_02;
    const csKnown = (csRole !== undefined && csRole !== null) || (csSeat !== undefined && csSeat !== null);
    const hasCS = isSuperAdmin || accountRole === 'dealer' || !csKnown
        || Number(csRole) > 0 || Number(csSeat) > 0;

    return (
        <Ctx.Provider
            value={{
                tenants,
                hostId,
                setHostId: setHostIdAndPersist,
                reload,
                loading,
                error,
                currentTenant,
                role,
                isAdmin,
                canOperate,
                accountRole,
                partnerSource,
                isSdx500: partnerSource === 'sdx500',
                isSuperAdmin,
                entitlement,
                hasCS,
                sharedHostId,
                isSharedBusiness,
            }}
        >
            {children}
        </Ctx.Provider>
    );
}

export function useTenant() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useTenant must be inside TenantProvider');
    return ctx;
}
